/**
 * Facility Resources API Service
 *
 * CRUD operations for facility resources: bays, simulators, rooms, courts.
 * API endpoints: /api/v2/facilities/{facilityId}/resources
 */
import {
  schedulingFetchDelete,
  schedulingFetchGet,
  schedulingFetchPatch,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";

export type ResourceKind = "bookable" | "location";

// Response from API. The request layer camel-cases every key it receives, so
// `resource_category_id` arrives here as `resourceCategoryId`.
export interface ResourceCategoryResponse {
  id: number;
  title: string;
  kind: ResourceKind;
  // The admin rank on the Resources page. Null until the first reorder.
  orderNumber?: number | null;
  facilityId: string;
}

export interface ScheduleResourceResponse {
  id: number;
  title: string;
  kind: ResourceKind;
  active: boolean;
  // The admin rank inside the section. Null until the first reorder.
  orderNumber?: number | null;
  resourceCategoryId: number;
  resourceCategory: ResourceCategoryResponse;
  facilityId: string;
}

// This version shows bookable resources only. A location from older data
// stays out of every list that the web shows.
export function isShownResource(resource: { kind?: ResourceKind }): boolean {
  return resource.kind !== "location";
}

type TitledResource = { title: string; kind?: ResourceKind };

/**
 * The titles of the resources a booking takes, from a booking or a purchase
 * item response. The service sends the list only while the facility has the
 * feature on, so the answer is empty with the feature off.
 */
export function resourceTitles(booking: {
  scheduleResources?: TitledResource[];
  schedule_resources?: TitledResource[];
} | null | undefined): string[] {
  const resources = booking?.scheduleResources ?? booking?.schedule_resources ?? [];

  return resources.filter(isShownResource).map((resource) => resource.title);
}

// A resource with the fields of the athlete label. The request layer
// camel-cases most answers, but a raw purchase item can keep snake_case keys.
type LabelledResource = TitledResource & {
  id?: number;
  orderNumber?: number | null;
  order_number?: number | null;
  resourceCategory?: { title?: string | null } | null;
  resource_category?: { title?: string | null } | null;
};

// One line of the athlete label: a section title and the titles of its bays.
export interface ResourceLine {
  section: string;
  titles: string;
}

function sectionTitle(resource: LabelledResource): string {
  return (resource.resourceCategory ?? resource.resource_category)?.title ?? "";
}

function compareText(a: string, b: string): number {
  if (a === b) {
    return 0;
  }

  return a < b ? -1 : 1;
}

// The same order as `ScheduleResource#pick_order_key` on the service: the
// section title (A to Z, case-insensitive), then the bays with a rank, lowest
// rank first, then the title, then the id.
function comparePickOrder(a: LabelledResource, b: LabelledResource): number {
  const rankA = a.orderNumber ?? a.order_number;
  const rankB = b.orderNumber ?? b.order_number;

  return (
    compareText(sectionTitle(a).toLowerCase(), sectionTitle(b).toLowerCase()) ||
    (rankA == null ? 1 : 0) - (rankB == null ? 1 : 0) ||
    (rankA ?? 0) - (rankB ?? 0) ||
    compareText(a.title.toLowerCase(), b.title.toLowerCase()) ||
    (a.id ?? 0) - (b.id ?? 0)
  );
}

/**
 * The resource label that an athlete sees, the same as the label in the
 * lesson mails (`LessonSchedule#resource_lines`). The label has one line for
 * each section, and the line holds the bay titles of that section, joined by
 * ", ". Many sections have a "Court 1", so the section title makes the bay
 * clear. The list leaves out a location. Coach, scheduler and admin screens
 * show the bay titles only (see `resourceTitles`).
 */
export function resourceLines(booking: {
  scheduleResources?: LabelledResource[];
  schedule_resources?: LabelledResource[];
} | null | undefined): ResourceLine[] {
  const resources = booking?.scheduleResources ?? booking?.schedule_resources ?? [];
  const sections = new Map<string, string[]>();

  [...resources]
    .filter(isShownResource)
    .sort(comparePickOrder)
    .forEach((resource) => {
      const section = sectionTitle(resource);
      sections.set(section, [...(sections.get(section) ?? []), resource.title]);
    });

  return Array.from(sections, ([section, titles]) => ({
    section,
    titles: titles.join(", "),
  }));
}

/**
 * The text of one label line: "Courts: Court 1". A line with no section (the
 * service always sends one) shows the bay titles alone.
 */
export function resourceLineText(line: ResourceLine): string {
  return line.section ? `${line.section}: ${line.titles}` : line.titles;
}

/**
 * Compares two records in the order an admin sets on the Resources page. A
 * record with a rank comes first, lowest rank first. A record with no rank
 * (made after the last reorder) comes after them. A tie goes by title.
 */
export function compareByAdminOrder(
  a: { orderNumber?: number | null; title: string },
  b: { orderNumber?: number | null; title: string }
): number {
  const rankA = a.orderNumber ?? Number.MAX_SAFE_INTEGER;
  const rankB = b.orderNumber ?? Number.MAX_SAFE_INTEGER;

  return rankA - rankB || a.title.localeCompare(b.title);
}

/**
 * The order of the Resources page: the sections in admin order, then the bays
 * of each section in admin order. The rank of a bay counts only inside its
 * section, so a sort on the bay rank alone mixes the sections.
 */
type RankedBay = {
  orderNumber?: number | null;
  title: string;
  resourceCategory: { orderNumber?: number | null; title: string };
};

export function compareBaysInAdminOrder(a: RankedBay, b: RankedBay): number {
  return (
    compareByAdminOrder(a.resourceCategory, b.resourceCategory) ||
    compareByAdminOrder(a, b)
  );
}

/**
 * The bays that take a booking, in the order of the Resources page. The list
 * leaves out a location and an inactive bay.
 */
export function activeBaysInAdminOrder(
  resources: ScheduleResourceResponse[]
): ScheduleResourceResponse[] {
  return resources
    .filter((resource) => resource.kind === "bookable" && resource.active)
    .sort(compareBaysInAdminOrder);
}

// Request payload for create/update (snake_case for API)
export interface ScheduleResourcePayload {
  title?: string;
  resource_category_id?: number;
  active?: boolean;
}

/**
 * GET /api/v2/facilities/{facilityId}/resources
 * Fetch the resources of a facility (in admin order, then by title)
 */
export async function getFacilityResources(
  facilityId: string
): Promise<ScheduleResourceResponse[]> {
  const response = await schedulingFetchGet<ScheduleResourceResponse[]>(
    `facilities/${facilityId}/resources`
  );

  return Array.isArray(response) ? (response as ScheduleResourceResponse[]) : [];
}

/**
 * POST /api/v2/facilities/{facilityId}/resources
 * Create a resource
 */
export async function createFacilityResource(
  facilityId: string,
  payload: ScheduleResourcePayload
): Promise<ScheduleResourceResponse> {
  return schedulingFetchPost<ScheduleResourceResponse>(
    `facilities/${facilityId}/resources`,
    payload
  );
}

/**
 * PATCH /api/v2/facilities/{facilityId}/resources/{resourceId}
 * Update a resource
 */
export async function updateFacilityResource(
  facilityId: string,
  resourceId: number,
  payload: ScheduleResourcePayload
): Promise<ScheduleResourceResponse> {
  return schedulingFetchPatch<ScheduleResourceResponse>(
    `facilities/${facilityId}/resources/${resourceId}`,
    payload
  );
}

/**
 * DELETE /api/v2/facilities/{facilityId}/resources/{resourceId}
 * Remove a resource. The API refuses when a booking option uses it, or when it
 * has a future booking.
 */
export async function deleteFacilityResource(
  facilityId: string,
  resourceId: number
): Promise<void> {
  await schedulingFetchDelete(
    `facilities/${facilityId}/resources/${resourceId}`
  );
}

// The order of the Resources page. Each list holds every id the page shows,
// in page order.
export interface ResourceOrderPayload {
  resourceCategoryIds: number[];
  scheduleResourceIds: number[];
}

/**
 * POST /api/v2/facilities/{facilityId}/resources/sort
 * Saves the order of the sections and of the resources in one request. Both
 * keys are on the keep list of `app/utils/request/model`, so an empty list
 * reaches the API.
 */
export async function sortFacilityResources(
  facilityId: string,
  order: ResourceOrderPayload
): Promise<void> {
  await schedulingFetchPost(`facilities/${facilityId}/resources/sort`, {
    resource_category_ids: order.resourceCategoryIds,
    schedule_resource_ids: order.scheduleResourceIds,
  });
}
